import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    TextField,
} from '@mui/material';
import { useAppDispatch } from '../store/hooks';
import ToastWrapper from './ToastWrapper';
import { useToastMessage } from '../hooks/useToastMessage';
import { OnboardDeviceHardwareConfiguration, ToastType } from '../interfaces';
import { loadOnboardDevicesHardwareConfigurations } from '../store/onboardDeviceHardwareConfigurations.reducer';
import { postOnboardDevicesHardwareConfiguration } from '../api/postOnboardDevicesHardwareConfiguration';
import { putOnboardDevicesHardwareConfiguration } from '../api/putOnboardDevicesHardwareConfiguration';
import NamedUrlField from './NamedUrlField';

export interface UpsertOnboardDevicesHardwareConfigurationDialogProps {
    open: boolean;
    onClose: () => void;
    configuration: OnboardDeviceHardwareConfiguration;
}

enum Mode {
    New = 'new',
    Edit = 'edit',
}

export const UpsertOnboardDevicesHardwareConfigurationDialog = ({
    open,
    onClose,
    configuration,
}: UpsertOnboardDevicesHardwareConfigurationDialogProps) => {
    const dispatch = useAppDispatch();
    const [mode, setMode] = useState(Mode.New);

    const initialValues = {
        id: configuration.id,
        name: configuration.name || '',
        documentationUrl: configuration.documentation?.url,
        documentationUrlName: configuration.documentation?.name,
        comment: configuration.comment || '',
        createdAt: configuration.createdAt,
        createdBy: configuration.createdBy,
        updatedAt: configuration.updatedAt || '',
        updatedBy: configuration.updatedBy || '',
    };

    useEffect(() => {
        const currentMode = initialValues.id ? Mode.Edit : Mode.New;
        setMode(currentMode);
    }, []);

    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();

    const validateForm = (values: any) => {
        const errors: any = {};

        if (!values.name) {
            errors.name = 'Required';
        }

        return errors;
    };

    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        try {
            const request = {
                name: values.name,
                documentation: values.documentationUrl
                    ? {
                          url: values.documentationUrl,
                          name: values.documentationUrlName,
                      }
                    : null,
                comment: values.comment,
            };

            if (mode === Mode.New) {
                await postOnboardDevicesHardwareConfiguration(request);
            } else if (mode === Mode.Edit) {
                await putOnboardDevicesHardwareConfiguration(initialValues.id!, request);
            }

            dispatch(loadOnboardDevicesHardwareConfigurations());
            onClose();
        } catch (error) {
            setToastMessage({
                message: `Під час створення конфігурації відбулася помилка`,
                type: ToastType.WARNING,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>
                {mode === Mode.New ? 'Add' : 'Edit'} Onboard Devices Hardware Configuration
            </DialogTitle>
            <DialogContent>
                <Formik
                    validate={validateForm}
                    onSubmit={handleSubmit}
                    initialValues={{
                        ...initialValues,
                    }}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form>
                            <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                                {mode === Mode.Edit && (
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="id"
                                            label="ID"
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </FormControl>
                                )}

                                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                    <Field
                                        as={TextField}
                                        name="name"
                                        label="Configuration Name"
                                        variant="outlined"
                                        size="small"
                                        error={touched.name && !!errors.name}
                                        helperText={touched.name && errors.name}
                                    />
                                </FormControl>

                                <NamedUrlField
                                    urlFieldName="documentationUrl"
                                    urlFieldLabel="Documentation Link"
                                    nameFieldName="documentationUrlName"
                                    nameFieldLabel="Link Name"
                                    urlFieldError={touched.documentationUrl && !!errors.documentationUrl}
                                    urlFieldHelperText={touched.documentationUrl && errors.documentationUrl}
                                />

                                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                    <Field
                                        as={TextField}
                                        name="comment"
                                        label="Comment"
                                        variant="outlined"
                                        size="small"
                                    />
                                </FormControl>

                                {mode === Mode.Edit && (
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="createdAt"
                                            label="Created At"
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </FormControl>
                                )}

                                {mode === Mode.Edit && (
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="createdBy"
                                            label="Created By"
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </FormControl>
                                )}

                                {mode === Mode.Edit && (
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="updatedAt"
                                            label="Updated At"
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </FormControl>
                                )}

                                {mode === Mode.Edit && (
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="updatedBy"
                                            label="Updated By"
                                            fullWidth={true}
                                            size="small"
                                            variant="filled"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </FormControl>
                                )}

                                <DialogActions>
                                    <Button size="small" variant="outlined" onClick={onClose} color="error">
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        size="small"
                                        variant="outlined"
                                        disabled={isSubmitting}
                                        color="primary"
                                    >
                                        Save
                                        {isSubmitting && (
                                            <CircularProgress style={{ marginLeft: '5px' }} size={15} />
                                        )}
                                    </Button>
                                </DialogActions>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={3000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </Dialog>
    );
};
